// Updated Login.js with Chakra UI styling and improved layout

import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Heading,
  Text,
  Divider,
  useToast,
  VStack,
} from "@chakra-ui/react";
import { auth, db } from "../firebase";
import {
  signInWithEmailAndPassword,
  GoogleAuthProvider,
  signInWithPopup,
} from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";

export default function Login() {
  const [formData, setFormData] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRedirect = async (user) => {
    const userRef = doc(db, "users", user.uid);
    const snap = await getDoc(userRef);

    if (!snap.exists()) {
      await setDoc(userRef, {
        email: user.email,
        role: "user",
        createdAt: new Date(),
      });
    }

    const updatedSnap = await getDoc(userRef);
    const userData = updatedSnap.data();
    const role = userData.role || "user";

    navigate(role === "admin" ? "/admin/dashboard" : "/user/dashboard");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { email, password } = formData;
      const userCred = await signInWithEmailAndPassword(auth, email, password);
      await handleRedirect(userCred.user);
    } catch (err) {
      toast({ title: "Login failed", description: err.message, status: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await handleRedirect(result.user);
    } catch (err) {
      toast({ title: "Google sign-in failed", description: err.message, status: "error" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Flex minH="100vh" align="center" justify="center" bg="gray.50" px={4}>
      <Box w="full" maxW="md" bg="white" p={8} borderRadius="xl" shadow="lg">
        <Heading mb={6} textAlign="center" color="teal.600">
          Log In to Serena
        </Heading>

        <form onSubmit={handleSubmit}>
          <VStack spacing={4}>
            <FormControl id="email" isRequired>
              <FormLabel>Email</FormLabel>
              <Input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
              />
            </FormControl>

            <FormControl id="password" isRequired>
              <FormLabel>Password</FormLabel>
              <Input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
              />
            </FormControl>

            <Button
              type="submit"
              colorScheme="teal"
              width="full"
              isLoading={loading}
              loadingText="Logging in..."
            >
              Login
            </Button>
          </VStack>
        </form>

        <Divider my={6} />

        <Button
          onClick={handleGoogleLogin}
          colorScheme="gray"
          variant="outline"
          width="full"
          isLoading={loading}
          loadingText="Please wait..."
        >
          Continue with Google
        </Button>

        <Text fontSize="sm" textAlign="center" mt={6}>
          By logging in, you agree to our{' '}
          <Link to="/app/privacy" style={{ color: "#319795", fontWeight: 500 }}>
            Privacy Policy
          </Link>
          .
        </Text>

        <Text mt={4} fontSize="sm" textAlign="center">
          Don’t have an account?{' '}
          <Link to="/register" style={{ color: "#319795", fontWeight: 500 }}>
            Register here
          </Link>
        </Text>
      </Box>
    </Flex>
  );
}
